import * as React from 'react';
import { CheckCircle2, Download, FileUp, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { fileToBase64 } from '@/components/apple-pay/byok-ui';

/**
 * Guided "we manage the keys" certificate setup, shared by the merchant page
 * and the account-manager tab. Per certificate type the user only ever:
 *
 *   1. downloads a ready-made signing request,
 *   2. uploads it in the Apple Developer portal,
 *   3. uploads the `.cer` Apple issues back here.
 *
 * Keys, p12 assembly, and passwords are handled server-side — none of it is
 * visible in this UI.
 */

export type CsrSlotStatus = 'not_started' | 'awaiting_certificate' | 'certificate_uploaded';

export interface CsrSetupState {
  type: 'payment_processing' | 'merchant_identity';
  status: CsrSlotStatus | string;
  /** The persisted public signing request, so it can be re-downloaded without
   * regenerating the keypair. Present once generated. */
  csrPem?: string | null;
  csrFileName?: string | null;
}

const SLOT_LABELS: Record<CsrSetupState['type'], { title: string; hint: string }> = {
  payment_processing: {
    title: 'Payment Processing Certificate',
    hint: 'Apple portal → your Merchant ID → "Payment Processing Certificate" → Create Certificate',
  },
  merchant_identity: {
    title: 'Merchant Identity Certificate',
    hint: 'Apple portal → your Merchant ID → "Merchant Identity Certificate" → Create Certificate',
  },
};

function CsrSlot({
  type,
  status,
  csrPem,
  csrFileName,
  busy,
  onGenerateCsr,
  onUploadCert,
}: {
  type: CsrSetupState['type'];
  status: CsrSlotStatus | string;
  csrPem?: string | null;
  csrFileName?: string | null;
  busy: boolean;
  /** Generates a NEW keypair + signing request (used for the first download and
   * for an explicit regenerate). */
  onGenerateCsr: (type: CsrSetupState['type']) => Promise<void>;
  onUploadCert: (type: CsrSetupState['type'], certificateBase64: string) => Promise<void>;
}) {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const labels = SLOT_LABELS[type];
  const started = status !== 'not_started';
  const fallbackName = `apple-pay-${type.replace('_', '-')}.certSigningRequest`;

  const handleFile = async (file: File | null) => {
    if (!file) return;
    await onUploadCert(type, await fileToBase64(file));
    if (fileRef.current) fileRef.current.value = '';
  };

  // Once generated we re-download the SAME persisted request (no server call →
  // no new keypair). Only the first download generates one.
  const handleDownload = async () => {
    if (started && csrPem) {
      downloadCsrFile(csrPem, csrFileName || fallbackName);
      return;
    }
    await onGenerateCsr(type);
  };

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{labels.title}</p>
        {status === 'certificate_uploaded' ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Certificate received
          </Badge>
        ) : status === 'awaiting_certificate' ? (
          <Badge variant="secondary">Waiting for Apple certificate</Badge>
        ) : (
          <Badge variant="outline">Not started</Badge>
        )}
      </div>

      <ol className="text-muted-foreground list-decimal space-y-1 pl-4 text-xs">
        <li>
          Download the signing request below
          {started ? ' (saved — you can download it again any time)' : ''}.
        </li>
        <li>
          In the Apple Developer portal, create this certificate and upload the request file.
          <span className="block">{labels.hint}</span>
        </li>
        <li>
          Download the certificate Apple issues (a <code>.cer</code> file) and upload it here.
        </li>
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={handleDownload}>
          <Download className="mr-2 h-4 w-4" />
          {started ? 'Download signing request' : 'Generate & download signing request'}
        </Button>
        {status !== 'certificate_uploaded' && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".cer,.pem,.crt,.der"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            <Button size="sm" disabled={busy || !started} onClick={() => fileRef.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" />
              Upload Apple certificate (.cer)
            </Button>
          </>
        )}
        {started && status !== 'certificate_uploaded' && (
          <Button
            variant="link"
            size="sm"
            disabled={busy}
            className="text-muted-foreground h-auto px-0 text-xs"
            onClick={() => onGenerateCsr(type)}
            title="Creates a new signing request — only if you haven't uploaded the current one to Apple yet"
          >
            Regenerate
          </Button>
        )}
      </div>
    </div>
  );
}

export function CsrCertificateSetup({
  setup,
  busy,
  replacing,
  onDownloadCsr,
  onUploadCert,
}: {
  /** Per-type setup state from the registration DTO (`certificateSetup`). */
  setup: CsrSetupState[];
  busy: boolean;
  /** When renewing, the certificate being replaced (shows a hint). */
  replacing?: boolean;
  onDownloadCsr: (type: CsrSetupState['type']) => Promise<void>;
  onUploadCert: (type: CsrSetupState['type'], certificateBase64: string) => Promise<void>;
}) {
  const slotFor = (type: CsrSetupState['type']) => setup.find((s) => s.type === type);

  return (
    <div className="space-y-3">
      {replacing && (
        <p className="text-muted-foreground flex items-center gap-1 text-xs">
          <RotateCw className="h-3.5 w-3.5" />
          Renewing: once both new certificates are uploaded, the selected certificate is replaced
          automatically.
        </p>
      )}
      <p className="text-muted-foreground text-xs">
        We handle the keys and the certificate packaging — you only exchange files with Apple. Both
        certificates activate together once uploaded. Your progress is saved — you can leave and finish each
        certificate later.
      </p>
      <div className="grid gap-3 lg:grid-cols-2">
        {(['payment_processing', 'merchant_identity'] as const).map((type) => {
          const slot = slotFor(type);
          return (
            <CsrSlot
              key={type}
              type={type}
              status={slot?.status ?? 'not_started'}
              csrPem={slot?.csrPem}
              csrFileName={slot?.csrFileName}
              busy={busy}
              onGenerateCsr={onDownloadCsr}
              onUploadCert={onUploadCert}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Trigger a browser download of the CSR text as a `.certSigningRequest` file. */
export function downloadCsrFile(csrPem: string, fileName: string): void {
  const blob = new Blob([csrPem], { type: 'application/pkcs10' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
